import type { PageTheme } from 'queries/graphql';
import { useFontOverride } from 'utils/font-override-context';
import { resolveLoadableFontFamily } from 'utils/font';

export default function GlobalStyleV2({
  backgroundColor,
  primaryColor,
  secondaryColor,
  headingColorDark,
  headingColorLight,
  textColorDark,
  textColorLight,
  colorPositive,
  colorNegative,
  starColor,
  starEmptyColor,
  fontSize,
  headingFont,
  textFont,
  linkColor,
  tagTextColor,
  customCss,
  // UI principles columns (added 2026-05-07). The three color columns
  // (`hoverColorPrimary`, `focusColorPrimary`, `disabledColor`) are
  // nullable — old variants generated before the migration don't have
  // them, and the CSS-var declarations below fall back to color-mix()
  // expressions so those variants still get sensible state colors
  // without backfill. `minTouchTargetPx` is `NOT NULL DEFAULT 44` so
  // it's always present post-migration.
  hoverColorPrimary,
  focusColorPrimary,
  disabledColor,
  minTouchTargetPx,
}: PageTheme) {
  const { fonts } = useFontOverride();

  const _headingFont = fonts[headingFont] || {};
  const _textFont = fonts[textFont] || {};

  if (process.env.NEXT_PUBLIC_USE_SYSTEM_FONTS === 'true') {
    _headingFont.fontFamily = 'var(--system-ui)';
    _textFont.fontFamily = 'var(--system-ui)';
  }

  return (
    <>
      <style jsx global>{`
        :root {
          --system-ui: system-ui, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif,
            'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';
          --primary-color: ${primaryColor};
          --secondary-color: ${secondaryColor};
          --heading-color-dark: ${headingColorDark};
          --heading-color-light: ${headingColorLight};
          --text-color-dark: ${textColorDark};
          --text-color-light: ${textColorLight};
          --text-color-grey-dark: #6a6a6a;
          --text-color-sea: #eaffed;
          --default-background-color: ${backgroundColor || 'transparent'};
          --background-color-grey: #f2f2f2;
          --color-positive: ${colorPositive};
          --color-negative: ${colorNegative};
          --tag-color: ${tagTextColor};
          --default-star-color: ${starColor};
          --default-star-empty-color: ${starEmptyColor};
          --border-color1: #333333;
          --border-color2: #707070;
          --link-text-color: ${linkColor};
          --default-checkout-button-color: #009c1b;
          /*
            UI principles enforcement layer.
            Falls back to color-mix() expressions so variants generated
            before the page_theme columns existed still get reasonable
            values. color-mix() lacks support in older Safari /
            embedded webviews — those degrade further to var(--primary-color)
            via the cascade (no visible state change but functional).
          */
          --hover-color-primary: ${hoverColorPrimary || 'color-mix(in srgb, var(--primary-color) 90%, black)'};
          --focus-color-primary: ${focusColorPrimary || 'var(--primary-color)'};
          --disabled-color: ${disabledColor || 'color-mix(in srgb, var(--primary-color) 40%, var(--default-background-color))'};
          --min-touch-target: ${minTouchTargetPx ?? 44}px;
          --heading-font-family: ${_headingFont?.fontFamily || resolveLoadableFontFamily(headingFont)};
          --text-font-family: ${_textFont?.fontFamily || resolveLoadableFontFamily(textFont)};
          --box-shadow-corner: 0px 3px 6px #00000029;
          --box-shadow-all: 0px 0px 6px 0 #00000029;
          --xs: 0.75rem;
          --sm: 0.875rem;
          --md: 1rem;
          --lg: 1.125rem;
          --xl: 1.25rem;
          --xl2: 1.5rem;
          --xl3: 1.875rem;
          --xl4: 2.25rem;
          --xl5: 3rem;
          --xl6: 3.75rem;
          --xl7: 4.5rem;
          --xl8: 6rem;
          --xl9: 8rem;
          --heading-weight-normal: ${_headingFont.weights?.normal};
          --heading-weight-bold: ${_headingFont.weights?.bold};
          --heading-weight-black: ${_headingFont.weights?.black};
          --text-weight-normal: ${_textFont.weights?.normal};
          --text-weight-bold: ${_textFont.weights?.bold};
          --text-weight-black: ${_textFont.weights?.black};
          --container-padding: 10px;
          --container-margin: 20px;
          --container-margin-mobile: 10px;
          --input-field-height: 50px;
          --input-field-margin-bottom: 15px;
          --tagline-letter-spacing: 2px;
          --border-radius: 5px;
          --disabled-button-background-color: #e0dddd;
          /* 
        The following z-index values must be
        used wherever using z-index is needed.
        Notes on the values: 
          - The input one must be used when the
            floating label effect is intended.
          - The overlay one must be used if an element
            of part of it should cover another element or 
            when a container has a shadow effect.
          - The sticky and modal ones should be used
            for sticky elements and modal containers.
        */
          --z-index-input: 1;
          --z-index-overlay: 10;
          --z-index-sticky: 20;
          --z-index-modal: 30;
          --divider-color: #e6e6e6;
        }
        * {
          box-sizing: border-box;
        }
        html {
          font-size: ${fontSize}px;
          scroll-behavior: smooth;
        }
        html,
        body {
          font-family: var(--text-font-family);
          color: var(--text-color-dark);
          overflow-x: visible;
        }
        button {
          font-family: var(--text-font-family);
          color: var(--text-color-dark);
        }
        html,
        body,
        h1,
        h2,
        h3,
        h4,
        p {
          margin: 0;
          padding: 0;
        }
        h1,
        h2,
        h3,
        h4,
        h5 {
          font-family: var(--heading-font-family);
        }
        h1 {
          font-size: var(--xl6);
        }
        h2 {
          font-size: var(--xl4);
        }
        h3 {
          font-size: var(--xl2);
        }
        h4 {
          font-size: var(--xl);
        }
        h5 {
          font-size: var(--lg);
        }
        p,
        li,
        a {
          font-size: var(--xl);
        }
        a {
          color: inherit;
          text-decoration: none;
        }
      `}</style>
      {customCss && <style dangerouslySetInnerHTML={{ __html: customCss }} />}
    </>
  );
}
